import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkles, Upload, ScrollText, ShieldAlert, ArrowUpRight, Trash2, Lock, Wand2, Scale, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2 } from "lucide-react";
import AISaintWordmark from "@/components/AISaintWordmark";

const SEVERITY_COLORS = {
  low: "bg-muted text-muted-foreground",
  medium: "bg-[hsl(32_87%_62%/0.2)] text-[hsl(32_60%_65%)]",
  high: "bg-destructive/20 text-[hsl(355_80%_75%)]",
};

const PRIORITY_COLORS = {
  low: "bg-muted text-muted-foreground",
  medium: "bg-primary/20 text-gold-soft",
  high: "bg-gold text-secondary-foreground",
};

function BalanceMeter({ score, label }) {
  const pct = Math.max(0, Math.min(100, score ?? 50));
  return (
    <div className="hoa-card p-6" data-testid="balance-meter">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-muted-foreground uppercase tracking-widest">Balance meter</div>
        <div className="text-sm font-semibold text-gold">{label}</div>
      </div>
      <div className="relative h-3 rounded-full overflow-hidden bg-muted">
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(355_78%_55%)] via-[hsl(var(--gold))] to-[hsl(200_65%_55%)]" />
        <div className="absolute top-0 bottom-0" style={{ left: `${pct}%` }}>
          <div className="w-1 h-5 -mt-1 bg-foreground rounded-full shadow-lg" />
        </div>
      </div>
      <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><TrendingDown className="w-3 h-3" /> HOA-favorable</span>
        <span className="font-mono text-gold">{pct}/100</span>
        <span className="flex items-center gap-1">Owner-favorable <TrendingUp className="w-3 h-3" /></span>
      </div>
    </div>
  );
}

function LockedGate() {
  return (
    <div className="hoa-card royal-ring p-10 text-center relative overflow-hidden" data-testid="ai-saint-locked">
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-primary/25 blur-3xl" />
      <div className="relative">
        <Lock className="w-8 h-8 text-gold mx-auto mb-3" />
        <h2 className="text-2xl font-semibold mb-2"><AISaintWordmark /> is a premium add-on</h2>
        <p className="no-gold text-foreground/75 max-w-xl mx-auto mb-6">
          Get past, present and proposed governing-document reviews with balance scoring and amendment
          suggestions. Start your 14-day free trial from Subscription → Add-ons.
        </p>
        <a href="/subscription" className="inline-flex items-center gap-2 bg-gold text-secondary-foreground px-5 py-3 rounded-full font-semibold hover:brightness-110 glow-gold">
          <Sparkles className="w-4 h-4" /> Start free trial
        </a>
      </div>
    </div>
  );
}

export default function AISaint() {
  const { user } = useAuth();
  const isBoard = user?.role === "admin" || user?.role === "board";
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [form, setForm] = useState({ title: "", doc_type: "bylaws", era: "present", state: "", content: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/governing-docs");
      setDocs(data);
      setLocked(false);
    } catch (err) {
      if (err.response?.status === 402) setLocked(true);
      else toast.error(err.response?.data?.detail || "Could not load");
    }
  };
  useEffect(() => { load(); }, []);

  const openDoc = async (id) => {
    const { data } = await api.get(`/governing-docs/${id}`);
    setSelected(data);
  };

  const submitDoc = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/governing-docs", { ...form, state: form.state || null });
      toast.success("Document uploaded");
      setUploadOpen(false);
      setForm({ title: "", doc_type: "bylaws", era: "present", state: "", content: "" });
      load();
      // Auto-open the newly uploaded doc
      setTimeout(() => openDoc(data.id), 100);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not upload");
    }
  };

  const runReview = async (focus = "balance") => {
    if (!selected) return;
    setReviewing(true);
    try {
      const { data } = await api.post(`/governing-docs/${selected.id}/review`, { focus });
      setSelected(data);
      toast.success("AI Saint completed the review");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI Saint could not complete the review");
    } finally {
      setReviewing(false);
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this document?")) return;
    try {
      await api.delete(`/governing-docs/${id}`);
      if (selected?.id === id) setSelected(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not delete"); }
  };

  if (locked) {
    return (
      <div className="space-y-6" data-testid="ai-saint-page">
        <div>
          <div className="flex items-center gap-2 text-gold text-sm font-medium mb-2">
            <Sparkles className="w-4 h-4" /> <AISaintWordmark />
          </div>
          <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight">Governing document review</h1>
        </div>
        <LockedGate />
      </div>
    );
  }

  const review = selected?.review;

  return (
    <div className="space-y-6" data-testid="ai-saint-page">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-gold text-sm font-medium mb-2">
            <Sparkles className="w-4 h-4" /> <AISaintWordmark /> · governing-doc review
          </div>
          <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight">Wisdom for your governance.</h1>
          <p className="no-gold text-foreground/75 mt-2 max-w-2xl">
            Upload bylaws, CC&Rs, rules, or proposed amendments. AI Saint returns a plain-English review with a
            balance score, risk flags, and amendment suggestions.
          </p>
        </div>
        {isBoard && (
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full h-11 bg-gold text-secondary-foreground hover:brightness-110" data-testid="upload-doc-btn">
                <Upload className="w-4 h-4 mr-2" /> Upload document
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Upload a governing document</DialogTitle></DialogHeader>
              <form onSubmit={submitDoc} className="space-y-4" data-testid="upload-form">
                <div>
                  <Label>Title</Label>
                  <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 h-11" placeholder="2024 CC&Rs — Restated" data-testid="doc-title" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Type</Label>
                    <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="doc-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bylaws">Bylaws</SelectItem>
                        <SelectItem value="ccrs">CC&Rs</SelectItem>
                        <SelectItem value="rules">Rules</SelectItem>
                        <SelectItem value="amendment">Amendment</SelectItem>
                        <SelectItem value="resolution">Resolution</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Era</Label>
                    <Select value={form.era} onValueChange={(v) => setForm({ ...form, era: v })}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="doc-era"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="past">Past</SelectItem>
                        <SelectItem value="present">Present</SelectItem>
                        <SelectItem value="proposed">Proposed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="mt-1.5 h-11" placeholder="LA" data-testid="doc-state" />
                  </div>
                </div>
                <div>
                  <Label>Document text</Label>
                  <Textarea required value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="mt-1.5 min-h-[220px]" placeholder="Paste bylaws / CC&Rs / rules text here (up to ~12,000 characters)…" data-testid="doc-content" />
                </div>
                <DialogFooter>
                  <Button type="submit" className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="doc-submit">
                    <Upload className="w-4 h-4 mr-2" /> Upload
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Docs list */}
        <div className="lg:col-span-4 space-y-3">
          <div className="text-xs uppercase tracking-widest text-gold-soft">Your documents</div>
          {docs.length === 0 && (
            <div className="hoa-card p-6 text-center text-muted-foreground text-sm" data-testid="docs-empty">
              No documents yet. Upload one to begin.
            </div>
          )}
          {docs.map((d) => (
            <button
              key={d.id}
              onClick={() => openDoc(d.id)}
              className={`w-full text-left hoa-card p-4 transition-colors ${selected?.id === d.id ? "border-gold royal-ring" : "hover:bg-accent/30"}`}
              data-testid={`doc-item-${d.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="no-gold text-sm font-semibold truncate">{d.title}</div>
                  <div className="text-xs text-muted-foreground capitalize">{d.doc_type} · {d.era}{d.state ? ` · ${d.state}` : ""}</div>
                </div>
                {d.review && <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0" />}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Uploaded {new Date(d.created_at).toLocaleDateString()}</div>
            </button>
          ))}
        </div>

        {/* Review panel */}
        <div className="lg:col-span-8">
          {!selected ? (
            <div className="hoa-card p-12 text-center text-muted-foreground" data-testid="no-doc-selected">
              Select a document to review, or upload a new one.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="hoa-card p-6">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h2 className="text-2xl font-semibold" data-testid="selected-doc-title">{selected.title}</h2>
                    <div className="text-xs text-muted-foreground capitalize">{selected.doc_type} · {selected.era}{selected.state ? ` · ${selected.state}` : ""} · by {selected.uploaded_by}</div>
                  </div>
                  {isBoard && (
                    <button onClick={() => del(selected.id)} className="text-muted-foreground hover:text-destructive" data-testid="del-selected-doc">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {isBoard && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Button size="sm" onClick={() => runReview("balance")} disabled={reviewing} data-testid="review-balance-btn">
                      <Wand2 className="w-4 h-4 mr-1.5" /> {reviewing ? "Reviewing…" : "Review · Balance"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => runReview("risk")} disabled={reviewing} data-testid="review-risk-btn">
                      Review · Risk
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => runReview("amendments")} disabled={reviewing} data-testid="review-amend-btn">
                      Review · Amendments
                    </Button>
                  </div>
                )}
              </div>

              {reviewing && (
                <div className="hoa-card p-6 flex items-center gap-3" data-testid="reviewing-indicator">
                  <div className="w-3 h-3 rounded-full bg-gold animate-pulse" />
                  <div className="text-sm">AI Saint is reading your document…</div>
                </div>
              )}

              {review && (
                <div className="space-y-4" data-testid="review-panel">
                  <div className="hoa-card p-6">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold-soft mb-2">
                      <ScrollText className="w-3.5 h-3.5" /> Summary
                    </div>
                    <p className="no-gold text-foreground/90 leading-relaxed" data-testid="review-summary">{review.summary}</p>
                  </div>

                  <BalanceMeter score={review.balance_score} label={review.balance_label} />

                  {review.risk_flags?.length > 0 && (
                    <div className="hoa-card p-6" data-testid="risk-flags-card">
                      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold-soft mb-3">
                        <ShieldAlert className="w-3.5 h-3.5" /> Risk flags
                      </div>
                      <div className="space-y-2">
                        {review.risk_flags.map((r, i) => (
                          <div key={`risk-${r.issue}-${i}`} className="flex items-start gap-3 p-3 rounded-lg bg-accent/30 border border-border" data-testid={`risk-flag-${i}`}>
                            <AlertTriangle className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${SEVERITY_COLORS[r.severity] || SEVERITY_COLORS.low}`}>{r.severity}</span>
                                <span className="no-gold text-sm font-semibold">{r.issue}</span>
                              </div>
                              {r.clause_hint && <div className="text-xs text-muted-foreground">Clause hint: {r.clause_hint}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-4">
                    {review.hoa_favorable_clauses?.length > 0 && (
                      <div className="hoa-card p-6" data-testid="hoa-favorable-card">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold-soft mb-2">
                          <TrendingDown className="w-3.5 h-3.5" /> HOA-favorable clauses
                        </div>
                        <ul className="space-y-1.5">
                          {review.hoa_favorable_clauses.map((c, i) => (
                            <li key={`hoa-fav-${i}-${String(c).slice(0, 24)}`} className="no-gold text-sm text-foreground/85">• {c}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {review.owner_favorable_clauses?.length > 0 && (
                      <div className="hoa-card p-6" data-testid="owner-favorable-card">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold-soft mb-2">
                          <TrendingUp className="w-3.5 h-3.5" /> Owner-favorable clauses
                        </div>
                        <ul className="space-y-1.5">
                          {review.owner_favorable_clauses.map((c, i) => (
                            <li key={`own-fav-${i}-${String(c).slice(0, 24)}`} className="no-gold text-sm text-foreground/85">• {c}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {review.ambiguities?.length > 0 && (
                    <div className="hoa-card p-6" data-testid="ambiguities-card">
                      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold-soft mb-2">
                        <Scale className="w-3.5 h-3.5" /> Ambiguities
                      </div>
                      <ul className="space-y-1.5">
                        {review.ambiguities.map((c, i) => (
                          <li key={`amb2-${i}-${String(c).slice(0, 20)}`} className="no-gold text-sm text-foreground/85">• {c}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {review.amendment_suggestions?.length > 0 && (
                    <div className="hoa-card p-6" data-testid="amend-card">
                      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold-soft mb-3">
                        <ArrowUpRight className="w-3.5 h-3.5" /> Amendment suggestions
                      </div>
                      <div className="space-y-2">
                        {review.amendment_suggestions.map((a, i) => (
                          <div key={`amend-${a.change}-${i}`} className="p-3 rounded-lg bg-accent/30 border border-border" data-testid={`amend-${i}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${PRIORITY_COLORS[a.priority] || PRIORITY_COLORS.low}`}>{a.priority}</span>
                              <span className="no-gold text-sm font-semibold">{a.change}</span>
                            </div>
                            {a.rationale && <div className="no-gold text-xs text-foreground/70">{a.rationale}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
